import { User } from "../types/Types";
import { isYouthOrEarlyStageMembership } from "./membershipTier";

/**
 * Azure AD / B2C object id from a user DTO. APIs may send `objectId` or `ObjectId`.
 */
export function resolvedObjectId(
  u: Pick<User, "objectId" | "id"> | null | undefined
): string {
  if (!u) return "";
  const r = u as User & { ObjectId?: string };
  return String(r.objectId ?? r.ObjectId ?? "").trim();
}

export function getAvatarUrl(user: User): string {
  return (
    (user as User & { professionalPhoto?: string }).professionalPhoto ||
    user.ProfessionalPhoto ||
    user.PersonalProfessionalPhoto ||
    user.personalProfessionalPhoto ||
    ""
  );
}

export function getInitials(user: User): string {
  const first = (user.firstName || "").trim().charAt(0).toUpperCase();
  const last = (user.lastName || "").trim().charAt(0).toUpperCase();
  return first && last ? `${first}${last}` : first || last || "?";
}

export function getRole(user: User): string {
  if (isYouthOrEarlyStageMembership(user.membershipName))
    return user.studyInterest || "";
  return user.personalPositionCompany || user.businessSectorIndustry || "";
}

export function getMembershipLabel(name: string): string {
  if (name === "Executive Membership") return "Executive Member";
  if (name === "Business Membership") return "Business Member";
  if (name === "Early Stage Membership") return "Early Stage Member";
  if (name === "Youth Membership") return "Youth Member";
  return name;
}

/** i18n key for connections list membership badge (`connections.memberBadge.*`). */
export function getMembershipBadgeKey(
  target: string | ConnectionTarget
): string {
  const membershipName =
    typeof target === "string"
      ? target
      : resolveConnectionTargetMembership(target);
  switch (normalizeMembership(membershipName)) {
    case "Youth Membership":
      return "connections.memberBadge.youth";
    case "Early Stage Membership":
      return "connections.memberBadge.earlyStage";
    case "Business Membership":
      return "connections.memberBadge.business";
    case "Executive Membership":
      return "connections.memberBadge.executive";
    default:
      return "";
  }
}

export type UpgradePromptKind = "executive" | "business";

/** Explorer segmentation values from step 2 (ExplorerSegmentation). */
const EXPLORER_SEGMENT_CONNECTION_LABEL: Record<string, string> = {
  "18_23_early_entrepreneur_sme": "Youth Member",
  "23_plus_early_entrepreneur_sme": "Business Member",
  "23_plus_established_sme_1million_plus_turnover": "Executive Member",
};

const EXPLORER_SEGMENT_MEMBERSHIP_TIER: Record<string, string> = {
  "18_23_early_entrepreneur_sme": "Youth Membership",
  "23_plus_early_entrepreneur_sme": "Business Membership",
  "23_plus_established_sme_1million_plus_turnover": "Executive Membership",
};

export type ConnectionTarget = Pick<User, "membershipName" | "explorerAttendeeType">;

export function resolveExplorerAttendeeType(
  user: Pick<User, "explorerAttendeeType" | "userCategory"> | null | undefined
): string {
  if (!user) return "";
  const r = user as User & { ExplorerAttendeeType?: string | null };
  return String(r.explorerAttendeeType ?? r.ExplorerAttendeeType ?? "").trim();
}

/**
 * Connections list badge only: Explorer segment → Youth / Business / Executive;
 * otherwise paid membership label.
 */
export function getConnectionMembershipDisplay(
  user: Pick<User, "membershipName" | "explorerAttendeeType" | "userCategory"> | null | undefined
): string {
  const segment = resolveExplorerAttendeeType(user);
  const fromSegment = segment ? EXPLORER_SEGMENT_CONNECTION_LABEL[segment] : "";
  if (fromSegment) return fromSegment;

  const r = user as User & { MembershipName?: string | null };
  const name = String(user?.membershipName ?? r?.MembershipName ?? "").trim();
  return getMembershipLabel(name);
}

/** Profile curved badge — same tier as connections list (segment → Youth / Business / Executive). */
export function getProfileMembershipBadgeTheme(
  user: ConnectionTarget | null | undefined
): { stroke: string; text: string } | null {
  const tier = resolveConnectionTargetMembership(user);
  switch (tier) {
    case "Youth Membership":
      return { stroke: "#18c2c6", text: "#Youth" };
    case "Early Stage Membership":
      return { stroke: "#18c2c6", text: "#EarlyStage" };
    case "Business Membership":
      return { stroke: "#1c57b9", text: "#Business" };
    case "Executive Membership":
      return { stroke: "#544bc1", text: "#Executive" };
    default:
      return null;
  }
}

/** Tier used for connection rules: Explorer segment first, else membershipName. */
export function resolveConnectionTargetMembership(
  user: ConnectionTarget | null | undefined
): string {
  const segment = resolveExplorerAttendeeType(user);
  const fromSegment = segment ? EXPLORER_SEGMENT_MEMBERSHIP_TIER[segment] : "";
  if (fromSegment) return fromSegment;

  const r = user as User & { MembershipName?: string | null };
  return String(user?.membershipName ?? r?.MembershipName ?? "").trim();
}

export function normalizeMembership(
  m: string | undefined
): "Youth Membership" | "Early Stage Membership" | "Business Membership" | "Executive Membership" | "" {
  const s = (m ?? "").trim().toLowerCase();
  if (s.includes("early stage")) return "Early Stage Membership";
  if (s.includes("youth")) return "Youth Membership";
  if (s.includes("business")) return "Business Membership";
  if (s.includes("executive")) return "Executive Membership";
  return "";
}

/** Youth and Early Stage are one connection tier (legacy Youth + new Early Stage). */
function isYouthConnectionTier(
  tier: ReturnType<typeof normalizeMembership>,
): boolean {
  return isYouthOrEarlyStageMembership(tier);
}

/**
 * Connection permission: who can send a connection request to whom.
 * Youth / Early Stage → same tier; Business → Youth + Early Stage + Business; Executive → all.
 * Explorer targets use segmentation tier (step 2 dropdown) when present.
 */
export function getConnectionPermission(
  currentMembership: string | undefined,
  target: ConnectionTarget | string
): {
  canConnect: boolean;
  upgradeMessage: string;
  upgradeKind: UpgradePromptKind | "none";
} {
  const targetMembership =
    typeof target === "string"
      ? target
      : resolveConnectionTargetMembership(target);
  const current = normalizeMembership(currentMembership);
  const targetTier = normalizeMembership(targetMembership);
  let canConnect = false;
  let upgradeMessage = "";
  let upgradeKind: UpgradePromptKind | "none" = "none";

  if (!current || !targetTier) {
    return { canConnect: false, upgradeMessage: "", upgradeKind: "none" };
  }

  if (current === targetTier) canConnect = true;
  else if (isYouthConnectionTier(current) && isYouthConnectionTier(targetTier))
    canConnect = true;
  else if (current === "Executive Membership") canConnect = true;
  else if (current === "Business Membership") {
    if (targetTier === "Youth Membership" || targetTier === "Early Stage Membership")
      canConnect = true;
    else {
      upgradeMessage = "Upgrade to Executive membership";
      upgradeKind = "executive";
    }
  } else if (current === "Youth Membership" || current === "Early Stage Membership") {
    if (targetTier === "Business Membership") {
      upgradeMessage = "Upgrade to Business membership";
      upgradeKind = "business";
    } else if (targetTier === "Executive Membership") {
      upgradeMessage = "Upgrade to Executive membership";
      upgradeKind = "executive";
    }
  }

  return { canConnect, upgradeMessage, upgradeKind };
}